/*
 * Copyright 2021 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { getEntityDisplayName } from '@agilelab/plugin-wb-builder-common';
import {
  CustomAlertApi,
  WbAutocomplete,
  customAlertApiRef,
} from '@agilelab/plugin-wb-platform';
import {
  Entity,
  getCompoundEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import {
  CatalogApi,
  catalogApiRef,
  humanizeEntityRef,
} from '@backstage/plugin-catalog-react';
import { useTheme } from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import { useCallback, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { useLatest } from 'react-use';
import useAsync from 'react-use/lib/useAsync';
import { FieldExtensionUiSchema } from '../../../extensions/types';
import { usePrevious } from '../../hooks/useEventStream';
import {
  extractCustomProperties,
  isHidden,
  UI_STYLE_SAFE_KEY,
} from '../../utils';
import { EntityPickerProps } from './schema';
import { useEntitiesMembership } from '../../hooks/useEntitiesMembership';

export const addKindFilter = (
  filter: any,
  uiSchema: FieldExtensionUiSchema<string | Entity, any>,
  _formContext?: any,
  _alertApi?: CustomAlertApi,
) => {
  if (uiSchema['ui:options']) {
    const allowedKinds = uiSchema['ui:options'].allowedKinds as string[];
    if (allowedKinds) {
      filter.kind = allowedKinds;
    }
  }

  return filter;
};

export const addFieldFilter = (
  filter: any,
  uiSchema: FieldExtensionUiSchema<string | Entity, any>,
  formContext?: any,
  alertApi?: CustomAlertApi,
) => {
  if (uiSchema['ui:filter']) {
    uiSchema['ui:filter'].forEach((uiFilter: any) => {
      const field = uiFilter.fieldName;

      if (!field) {
        alertApi?.post({
          message: `Parameter 'fieldName' is not provided under 'ui:filter'! Please check the template!`,
          severity: 'error',
        });
      }

      const entityPath = uiFilter.entityPath;

      if (!entityPath) {
        alertApi?.post({
          message: `Parameter 'entityPath' is not provided under 'ui:filter'! Please check the template!`,
          severity: 'error',
        });
      }

      if (field) {
        const entityPathValue = formContext[field];

        if (entityPathValue) {
          filter[entityPath] = entityPathValue;
        }
      }
    });
  }

  return filter;
};

function parseEntityRef(entityLike: any): string | null {
  try {
    return stringifyEntityRef(getCompoundEntityRef(entityLike));
  } catch (error) {
    return null;
  }
}

async function fetchEntities(
  uiSchema: FieldExtensionUiSchema<
    string | Entity,
    {
      allowedKinds?: string[];
      defaultKind?: string;
      defaultNamespace?: string | false;
      excludeValues?: string[];
      showOnlyUserOwnGroups?: boolean;
      storeRawEntity?: boolean;
      'ui:widget'?: string;
    }
  >,
  formContext: any,
  alertApi: CustomAlertApi,
  catalogApi: CatalogApi,
  getUserMembership: (options: {
    filter: {
      kind: string[];
    };
  }) => Promise<Entity[]>,
): Promise<Entity[]> {
  const allowedKinds = uiSchema['ui:options']?.allowedKinds || [];

  if (
    allowedKinds.some(
      kind => kind.toLowerCase() === 'user' || kind.toLowerCase() === 'group',
    ) &&
    uiSchema['ui:options']?.showOnlyUserOwnGroups
  ) {
    return getUserMembership({
      filter: addKindFilter({}, uiSchema, formContext, alertApi),
    });
  }

  const filter = [addKindFilter, addFieldFilter].reduce(
    (filterResult, filterFunction) =>
      filterFunction(filterResult, uiSchema, formContext, alertApi),
    {},
  );

  const entities = await catalogApi.getEntities(
    Object.keys(filter).length ? { filter } : undefined,
  );

  return entities?.items.filter(e => {
    const entityName = humanizeEntityRef(e, {
      defaultKind: uiSchema['ui:options']?.defaultKind,
      defaultNamespace: uiSchema['ui:options']?.defaultNamespace,
    });
    return uiSchema['ui:options']?.excludeValues
      ? !uiSchema['ui:options']?.excludeValues.includes(entityName)
      : entityName;
  });
}

/**
 * The underlying component that is rendered in the form for the `EntityPicker`
 * field extension.
 *
 * @public
 */
export const EntityPicker = (props: EntityPickerProps) => {
  const {
    onChange,
    schema: { title = 'Entity', description = 'An entity from the catalog' },
    required,
    uiSchema,
    rawErrors,
    formData,
    idSchema,
    formContext,
  } = props;

  // In the current implementation of rjsf, the onChange function is redefined on every formData update.
  // Using onChange directly as a dependency can cause unnecessary re-renders and increase the risk of race conditions (see: https://github.com/rjsf-team/react-jsonschema-form/issues/3367).
  // To avoid these issues, we use useLatest to always refer to the most recent onChange function without triggering excessive re-renders.
  const onChangeRef = useLatest(onChange);
  const defaultKind = uiSchema['ui:options']?.defaultKind as string | undefined;
  const storeRawEntity: boolean =
    (uiSchema['ui:options']?.storeRawEntity as boolean) ?? false;
  const defaultNamespace = uiSchema['ui:options']?.defaultNamespace;
  const alertApi = useApi(customAlertApiRef);
  const catalogApi = useApi(catalogApiRef);
  const { getMembersIdentities, identityLoading } = useEntitiesMembership();
  const [stateEntities, setEntities] = useState<Entity[]>();
  const [value, setValue] = useState<Entity | null | undefined>(undefined);

  const customProps = extractCustomProperties(uiSchema);

  const filterValuesKey = useMemo(() => {
    if (!uiSchema['ui:filter']) return '';
    return (uiSchema['ui:filter'] as Array<{ fieldName: string }>)
      .map(f => String(formContext?.[f.fieldName] ?? ''))
      .join('\x00');
  }, [uiSchema, formContext]);

  const prevFilterValuesKey = usePrevious(filterValuesKey);

  const { loading } = useAsync(async () => {
    if (identityLoading) {
      return;
    }
    const entityRef =
      typeof formData === 'string' ? formData : parseEntityRef(formData);
    // fetch the Entity from the passed value
    const formDataEntity: Entity | null = entityRef
      ? (await catalogApi.getEntityByRef(entityRef)) ?? null
      : null;

    // check if this field depends on the value of another one using the ui:filter flag
    // and if at least one value of the dependent ui:filter has changed
    const filtersChanged =
      uiSchema['ui:filter'] && filterValuesKey !== prevFilterValuesKey;

    // if a dependent field changed or if there are no cached entities
    if (filtersChanged || !stateEntities) {
      // load all the entities using the dependent filters
      const fetchedEntities = await fetchEntities(
        uiSchema,
        formContext,
        alertApi,
        catalogApi,
        getMembersIdentities,
      );
      // cache the loaded entities
      setEntities(fetchedEntities);
      // if the provided value is inside the fetched ones set it
      if (
        formDataEntity &&
        fetchedEntities.find(
          e =>
            humanizeEntityRef(e, {
              defaultKind,
              defaultNamespace,
            }) ===
            humanizeEntityRef(formDataEntity, {
              defaultKind,
              defaultNamespace,
            }),
        )
      ) {
        setValue(formDataEntity);
        return;
      }
      // if there is only one available entity, set it as default selected one
      const defaultValue =
        fetchedEntities?.length === 1 ? fetchedEntities[0] : null;
      if (defaultValue) {
        setValue(defaultValue);
        flushSync(() => {
          onChangeRef.current(
            storeRawEntity
              ? defaultValue
              : humanizeEntityRef(defaultValue, {
                  defaultKind,
                  defaultNamespace,
                }), // default ref
          );
        });
      } else if (filtersChanged) {
        // Only clear the selection if filters explicitly changed
        // Don't reset on initial mount if formData already has a value
        setValue(undefined);
        flushSync(() => {
          onChangeRef.current(undefined);
        });
      }
      // If !filtersChanged and no defaultValue, preserve existing formData value
      // This prevents losing values during step transitions or re-renders
      return;
    }
    if (formDataEntity !== undefined) {
      setValue(formDataEntity);
    }
  }, [filterValuesKey, formData, identityLoading, catalogApi]);

  const onSelect = useCallback(
    (_: any, val: Entity | string | undefined) => {
      if (typeof val !== 'string') {
        const ref = val
          ? humanizeEntityRef(val, { defaultKind, defaultNamespace })
          : undefined;
        setValue(val);

        // Always use flushSync to update formData synchronously
        // This is required for ui:filter to work (it compares prevFormContext vs formContext)
        // NOTE: We don't call notifyUpdate here because handleFormChange in useDependencyManagedForm
        // already calls notifyUpdate for all changed fields. Calling it twice causes issues.
        const valueToSave = storeRawEntity ? val : ref;
        flushSync(() => {
          onChangeRef.current(valueToSave);
        });
      }
    },
    [defaultKind, defaultNamespace, onChangeRef, storeRawEntity],
  );
  const disabled = uiSchema['ui:disabled'];
  const noPermissionWarning = stateEntities?.length === 0;
  const warningMessage = `There are no selectable entities for this picker. Please check your permissions.`;
  const theme = useTheme();

  const hasErrors =
    (rawErrors?.length > 0 && !value) || (noPermissionWarning && required);

  const color =
    noPermissionWarning && !hasErrors
      ? {
          color: theme.palette.warning.light,
        }
      : {};

  // the Autocomplete's "value" property needs to be "null" to create it in controlled mode (the status is kept outside)
  return (
    <FormControl
      style={{ display: isHidden(uiSchema) ? 'none' : undefined }}
      required={required}
    >
      <WbAutocomplete
        style={{
          width: '100%',
          height: '100%',
        }}
        error={hasErrors}
        small={uiSchema[UI_STYLE_SAFE_KEY]?.height === '32px'}
        disabled={(stateEntities && stateEntities.length <= 1) || disabled}
        id={idSchema?.$id}
        value={value || null}
        loading={loading}
        onChange={onSelect}
        options={stateEntities || []}
        autoSelect
        freeSolo={false}
        getOptionLabel={(option: Entity) => getEntityDisplayName(option)}
        getOptionSelected={(option: Entity, val: Entity) =>
          humanizeEntityRef(option, { defaultKind, defaultNamespace }) ===
          humanizeEntityRef(val, { defaultKind, defaultNamespace })
        }
        label={title}
        required={required}
        helperText={noPermissionWarning ? warningMessage : description}
        FormHelperTextProps={{
          style: { ...color },
        }}
        {...customProps}
      />
    </FormControl>
  );
};
